import {AuthPasswordPatternType} from '../constants';
import {getPasswordPatternObject} from './getPasswordPatternObject';
import {getMinimumPasswordLength} from './getMinimumPasswordLength';

const SPECIAL_CHARACTERS = [
	'!',
	'@',
	'#',
	'$',
	'%',
	'^',
	'&',
	'*',
	'(',
	')',
	'+',
	'=',
	'_',
	'-',
	'{',
	'}',
	'?',
	':',
	';',
	'<',
	'>',
	'.',
	'|',
	'[',
	']',
];

export type GetRegexFromPasswordPrefsParams = {
	ignoreLengthRequirement?: boolean;
};

/**
 * Get `RegExp` representing the password requirements if applicable
 * @see https://netx-dam.atlassian.net/wiki/spaces/KB/pages/1382810319/Password+and+Login+Security
 * @returns Password Requirements `RegExp`
 */
export const getRegexFromPasswordPrefs = ({
	ignoreLengthRequirement,
}: GetRegexFromPasswordPrefsParams = {}) => {
	const passwordLength = getMinimumPasswordLength();

	const passwordPatternObject = getPasswordPatternObject();

	if (!passwordPatternObject)
		return !ignoreLengthRequirement
			? // eslint-disable-next-line security/detect-non-literal-regexp
			  new RegExp(`(?:\\S){${passwordLength},}$`)
			: new RegExp('(?:\\S)$');

	const passwordPatternRegex = Object.entries(
		passwordPatternObject,
	).reduce<string>((accum, [patternType, patternLength]) => {
		switch (patternType) {
			case AuthPasswordPatternType.LOWERCASE_LETTERS:
				return `${accum}(?=(?:[^a-z]*[a-z]){${patternLength},})`;

			case AuthPasswordPatternType.NUMBER:
				return `${accum}(?=(?:\\D*\\d){${patternLength},})`;

			case AuthPasswordPatternType.SPECIAL_CHARACTERS: {
				const specialChars = `\\${SPECIAL_CHARACTERS.join('\\')}`;
				return `${accum}(?=(?:[^${specialChars}]*[${specialChars}]){${patternLength},})`;
			}

			case AuthPasswordPatternType.UPPERCASE_LETTERS:
				return `${accum}(?=(?:[^A-Z]*[A-Z]){${patternLength},})`;
		}
		return accum;
	}, '');

	return !ignoreLengthRequirement
		? // eslint-disable-next-line security/detect-non-literal-regexp
		  new RegExp(`(?:${passwordPatternRegex}\\S*)\\S{${passwordLength},}$`)
		: // eslint-disable-next-line security/detect-non-literal-regexp
		  new RegExp(`${passwordPatternRegex}`);
};

export default getRegexFromPasswordPrefs;
